import React, { useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CalendarClock } from 'lucide-react';
import { createReminderSchedule } from '../../../../api/collections';
import { showToast } from '../../../../utils/showToast';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Modal } from '../../../../components/ui/Modal';
import { Select } from '../../../../components/ui/Select';
import { Skeleton } from '../../../../components/ui/Skeleton';
import useCollectionsSetup from '../hooks/useCollectionsSetup';
import useIdempotencyKey from '../hooks/useIdempotencyKey';
import useReminderMessagePreview from '../hooks/useReminderMessagePreview';
import useReminderTemplates from '../hooks/useReminderTemplates';
import DeliveryStatusBanner from './DeliveryStatusBanner';
import ReminderSendPreview from './ReminderSendPreview';
import ReminderTemplateFields from './ReminderTemplateFields';
import WhatsappNumberSelect, { SelectedNumberWarnings } from './WhatsappNumberSelect';
import {
  expectedTemplateVersions,
  invalidTemplateChannels,
  isTemplateChanged,
  operatorDefaultTemplateIds,
  reminderSendErrorText,
  requestedTemplateIds,
} from '../utils/reminderTemplates';
import {
  MAX_SCHEDULE_DAYS_AHEAD,
  USUAL_SEND_HOURS,
  addDaysToDayKey,
  alreadyScheduledDates,
  defaultScheduleSendAt,
  defaultWhatsappNumber,
  formatHour,
  formatLocalDateTime,
  isCollectibleNote,
  isOutsideUsualSendHours,
  isSavedWhatsappMissing,
  localNowParts,
  scheduleSendAtError,
  selectedChannels,
  whatsappNumberOptions,
} from '../utils/collectionReminders';

/** Tope de notas por envío programado (el @ArrayMaxSize del DTO de billing). */
export const MAX_SCHEDULE_NOTES = 500;
const HOURS = Array.from({ length: 24 }, (_, h) => h);
// Pestaña "Programados" de /admin/cobro/cobranza, donde se cancela un programado.
const SCHEDULES_PATH = '/admin/cobro/cobranza?tab=programados';

const channelConfigured = (delivery, channel) =>
  !delivery || (channel === 'email' ? delivery.emailConfigured : delivery.whatsappConfigured);

/**
 * Programa un recordatorio de las notas seleccionadas en la lista para un día y hora LOCALES
 * del operador. La fecha y la hora viajan como `{ date: 'YYYY-MM-DD', hour }` y billing las
 * convierte con el huso del operador: el navegador puede estar en otra zona.
 *
 * La clave de idempotencia se genera al ABRIR el formulario y se reutiliza en cada reintento
 * del mismo envío: si la primera respuesta se perdió, billing devuelve lo ya programado en
 * vez de programarlo dos veces. Cuándo cambia: ver useIdempotencyKey.
 *
 * Con billing con plantillas se elige la plantilla de cada canal (arranca en la por defecto)
 * y al lado se ve el mensaje que saldría hoy, comercio por comercio, sin las notas que ya
 * esperan en otro programado cercano (la vista previa lleva la fecha y hora elegidas). La
 * versión de la plantilla queda CONGELADA al programar: editarla o archivarla después no
 * cambia el envío. Viaja la versión que mostró la vista previa (`expectedTemplateVersions`):
 * si otra persona la editó en el medio, billing responde 409 `template_changed` sin programar
 * nada, la vista previa se recarga y se pide confirmar de nuevo.
 *
 * La plantilla por defecto sale de los settings recargados en ESTA apertura: el modal queda
 * montado y, al reabrirlo, los de la anterior pueden estar viejos.
 *
 * @param {{ open: boolean, onClose: () => void, notes: object[],
 *           onScheduled?: (result: object) => void }} props
 */
export default function ScheduleReminderModal({ open, onClose, notes, onScheduled }) {
  const { t } = useTranslation();
  const baseId = useId();
  const setup = useCollectionsSetup({ enabled: open, withIntegrations: true });
  const templates = useReminderTemplates({ enabled: open });
  // Cada apertura es una intención nueva: clave nueva y formulario desde cero.
  const idempotency = useIdempotencyKey(open);
  const keyError = idempotency.keyError;
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  // Billing respondió 2xx sin crear nada porque cada nota+canal ya espera en otro programado:
  // fechas locales de esos programados (puede venir vacío si billing no las informa).
  const [alreadyScheduled, setAlreadyScheduled] = useState(null);
  // Canales cuya plantilla rechazó billing al programar (se limpia al editar).
  const [rejectedTemplates, setRejectedTemplates] = useState([]);
  // Comercio del que se ve el mensaje ("Ver mensaje de"); null = el primero de billing. No
  // es parte del envío: cambiarlo no toca la clave de idempotencia.
  const [previewCompanyId, setPreviewCompanyId] = useState(null);
  // Billing avisó que una plantilla cambió desde la vista previa: se confirma de nuevo con
  // la vista previa recargada (no antes de que llegue).
  const [templateChanged, setTemplateChanged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(null);
    setError(null);
    setAlreadyScheduled(null);
    setRejectedTemplates([]);
    setPreviewCompanyId(null);
    setTemplateChanged(false);
  }, [open]);

  const view = setup.view;
  const timeZone = view?.operatorTimeZone;
  const delivery = view?.delivery;
  const templateDefaults = useMemo(() => operatorDefaultTemplateIds(view?.settings), [view]);
  const savedWhatsappId = view?.settings?.autoReminders?.whatsappIntegrationId ?? null;
  const waOptions = useMemo(
    () => whatsappNumberOptions(setup.integrations, savedWhatsappId),
    [setup.integrations, savedWhatsappId],
  );

  // Valores por defecto cuando llega la configuración: próxima hora en punto del operador,
  // los canales de la regla automática que el entorno puede enviar, el número guardado y la
  // plantilla por defecto de cada canal. Recién con los settings de esta apertura: mientras
  // recargan, `view` sigue siendo el de la anterior.
  useEffect(() => {
    if (!open || !view || form || setup.loading) return;
    const ruleChannels = view.settings?.autoReminders?.channels ?? { email: true, whatsapp: false };
    const whatsappIntegrationId = defaultWhatsappNumber(waOptions, savedWhatsappId);
    setForm({
      ...defaultScheduleSendAt(timeZone),
      channels: {
        email: ruleChannels.email !== false && channelConfigured(view.delivery, 'email'),
        whatsapp:
          ruleChannels.whatsapp === true &&
          channelConfigured(view.delivery, 'whatsapp') &&
          !!whatsappIntegrationId,
      },
      whatsappIntegrationId,
      templateIds: templateDefaults,
    });
  }, [open, view, form, setup.loading, timeZone, waOptions, savedWhatsappId, templateDefaults]);

  const collectible = useMemo(() => (notes ?? []).filter(isCollectibleNote), [notes]);
  const collectibleIds = useMemo(() => collectible.map((n) => n._id), [collectible]);
  const excluded = (notes?.length ?? 0) - collectible.length;
  const tooMany = collectible.length > MAX_SCHEDULE_NOTES;
  // Fecha y hora de la vista previa, solo si son válidas (si no, el formulario lo avisa al
  // programar y la vista previa sale sin descontar otros programados).
  const previewSendAt = useMemo(() => {
    if (!form) return null;
    const sendAt = { date: form.date, hour: Number(form.hour) };
    return scheduleSendAtError(sendAt, timeZone) ? null : sendAt;
  }, [form, timeZone]);

  // Un comercio por página: la vista previa muestra un mensaje a la vez y billing devuelve
  // igual la lista completa de comercios para el selector.
  const preview = useReminderMessagePreview({
    noteIds: collectibleIds,
    channels: form?.channels ?? null,
    templateIds: form?.templateIds,
    companyId: previewCompanyId,
    limit: 1,
    sendAt: previewSendAt,
    enabled: open && !!form && !!templateDefaults && !tooMany,
  });
  // Un 400 de la vista previa por una plantilla archivada o de otro canal: se recarga el
  // catálogo para que el selector la muestre como no disponible.
  const previewRejected = invalidTemplateChannels(preview.error).length > 0;
  const reloadTemplates = templates.reload;
  useEffect(() => {
    if (previewRejected) reloadTemplates();
  }, [previewRejected, reloadTemplates]);
  const invalidTemplates = [
    ...new Set([...rejectedTemplates, ...(preview.pending ? [] : invalidTemplateChannels(preview.error))]),
  ];
  const selectableNumbers = waOptions.filter((o) => o.selectable);
  // El número configurado ya no aparece (borrado o inactivo): WhatsApp arranca sin tildar en
  // vez de salir por otro número. Si fallaron los números, no se sabe: no se afirma nada.
  const savedNumberMissing =
    !setup.integrationsError && isSavedWhatsappMissing(waOptions, savedWhatsappId);
  const today = view ? localNowParts(timeZone).date : null;

  // Todo cambio (también la plantilla) pasa por acá: tras un rechazo de billing es una
  // intención nueva y lleva clave nueva (ver useIdempotencyKey).
  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setError(null);
    setAlreadyScheduled(null);
    setRejectedTemplates([]);
    setTemplateChanged(false);
    idempotency.onEdited();
  };

  const handleSubmit = async () => {
    const channels = selectedChannels(form.channels);
    const sendAt = { date: form.date, hour: Number(form.hour) };
    const problem =
      channels.length === 0
        ? 'Elegí al menos un canal (correo o WhatsApp).'
        : form.channels.whatsapp && !form.whatsappIntegrationId
          ? 'Elegí el número de WhatsApp desde el que se envían los recordatorios.'
          : scheduleSendAtError(sendAt, timeZone);
    if (problem) {
      setError(t(problem));
      return;
    }
    const body = {
      noteIds: collectibleIds,
      sendAt,
      channels: { email: !!form.channels.email, whatsapp: !!form.channels.whatsapp },
      idempotencyKey: idempotency.key,
    };
    if (form.channels.whatsapp) body.whatsappIntegrationId = form.whatsappIntegrationId;
    const templateIds = requestedTemplateIds(body.channels, form.templateIds);
    if (templateIds) body.templateIds = templateIds;
    const expectedVersions = expectedTemplateVersions(body.channels, form.templateIds, preview.data?.items);
    if (expectedVersions) body.expectedTemplateVersions = expectedVersions;

    setSubmitting(true);
    setError(null);
    setAlreadyScheduled(null);
    setTemplateChanged(false);
    try {
      const { data } = await createReminderSchedule(body);
      // Billing no duplica una nota+canal que ya espera en otro programado cercano (el
      // enfriamiento la descartaría igual): `notes`/`dispatches` cuentan solo lo creado y lo
      // omitido va en `alreadyScheduled`. Si no se creó nada, no es un éxito: el formulario
      // queda abierto con la selección y explica qué programado lo tiene.
      const omitted = Array.isArray(data?.alreadyScheduled) ? data.alreadyScheduled : [];
      if (data?.dispatches === 0 && omitted.length > 0) {
        setAlreadyScheduled(alreadyScheduledDates(omitted));
        return;
      }
      const when = formatLocalDateTime(data?.sendAtLocal) || `${form.date} ${formatHour(form.hour)}`;
      showToast(
        data?.replayed
          ? t('Ese recordatorio ya estaba programado para el {{fecha}}.', { fecha: when })
          : t('Recordatorio programado para el {{fecha}} ({{n}} notas).', { fecha: when, n: data?.notes ?? 0 }),
        'success',
      );
      if (omitted.length > 0) {
        showToast(
          t('{{n}} envíos (nota y canal) no se programaron: ya tenían un recordatorio pendiente por el mismo canal en otro programado.', {
            n: omitted.length,
          }),
          'warning',
        );
      }
      const skipped = data?.skipped?.length ?? 0;
      if (skipped > 0) {
        showToast(t('{{n}} notas no se programaron porque ya no son cobrables.', { n: skipped }), 'warning');
      }
      onScheduled?.(data);
    } catch (err) {
      // El formulario queda abierto. Si no se sabe si se programó, con la MISMA clave:
      // reintentar no duplica el envío.
      idempotency.onRequestFailed(err);
      const rejected = invalidTemplateChannels(err);
      if (rejected.length > 0) {
        setRejectedTemplates(rejected);
        reloadTemplates();
      }
      if (isTemplateChanged(err)) {
        // No se programó nada: con la versión nueva a la vista es otra intención (clave
        // nueva si la anterior fue rechazada; se conserva si un intento previo quedó incierto).
        setTemplateChanged(true);
        idempotency.onEdited();
        preview.refresh();
      }
      setError(reminderSendErrorText(err, t, 'No se pudo programar el recordatorio.'));
    } finally {
      setSubmitting(false);
    }
  };

  const blocked =
    keyError ||
    !form ||
    collectible.length === 0 ||
    tooMany ||
    (delivery && !delivery.schedulerEnabled) ||
    (templateChanged && preview.pending);

  let body;
  if (!form && setup.loading) {
    body = (
      <div className="space-y-3" role="status" aria-live="polite">
        <span className="sr-only">{t('Cargando…')}</span>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  } else if (setup.error && !view) {
    body = (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
        <span>
          {setup.error.status === 403
            ? t('Tu rol no tiene permiso para gestionar la cobranza.')
            : setup.error.message || t('No se pudo cargar la configuración de cobranza.')}
        </span>
        {setup.error.status !== 403 && (
          <Button size="sm" variant="outline" onClick={setup.reload}>
            {t('Reintentar')}
          </Button>
        )}
      </div>
    );
  } else if (form) {
    const fields = (
      <div className="min-w-0 space-y-4">
        <DeliveryStatusBanner delivery={delivery} channels={selectedChannels(form.channels)} />
        {keyError && (
          <p className="rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
            {t('Este navegador no permite generar la clave del envío. Actualizalo o probá con otro.')}
          </p>
        )}
        {excluded > 0 && (
          <p className="bill-note m-0">
            {t('{{n}} notas no son cobrables (pagadas, en revisión, anuladas o sin monto a cobrar) y no se incluirán.', { n: excluded })}
          </p>
        )}
        {tooMany && (
          <p className="rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error">
            {t('Máximo {{n}} notas por envío programado: achicá la selección.', { n: MAX_SCHEDULE_NOTES })}
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label htmlFor={`${baseId}-date`} className="text-sm font-medium text-fg">
              {t('Fecha')}
            </label>
            <Input
              id={`${baseId}-date`}
              type="date"
              value={form.date}
              min={today ?? undefined}
              max={today ? addDaysToDayKey(today, MAX_SCHEDULE_DAYS_AHEAD) : undefined}
              onChange={(e) => update({ date: e.target.value })}
            />
          </div>
          <Select
            label={t('Hora')}
            value={String(form.hour)}
            onChange={(e) => update({ hour: Number(e.target.value) })}
          >
            {HOURS.map((h) => (
              <option key={h} value={String(h)}>
                {formatHour(h)}
              </option>
            ))}
          </Select>
        </div>
        <p className="-mt-2 text-xs text-fg-muted">
          {t('Hora de {{zona}}, la zona del operador. Sale en el ciclo de envío de esa hora (unos minutos).', {
            zona: timeZone,
          })}
        </p>
        {/* Billing acepta cualquier hora en un programado: fuera de horario se avisa. */}
        {isOutsideUsualSendHours(form.hour) && (
          <p className="flex items-start gap-1.5 rounded-md bg-status-warning-soft px-2.5 py-1.5 text-xs text-status-warning">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>
              {t('Es fuera del horario habitual ({{desde}} a {{hasta}}): el comercio lo recibirá a esa hora.', {
                desde: formatHour(USUAL_SEND_HOURS.from),
                hasta: formatHour(USUAL_SEND_HOURS.to),
              })}
            </span>
          </p>
        )}

        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium text-fg">{t('Canales')}</legend>
          <div className="flex flex-wrap gap-4">
            {['email', 'whatsapp'].map((channel) => {
              const configured = channelConfigured(delivery, channel);
              return (
                <label
                  key={channel}
                  className={`flex items-center gap-2 text-sm text-fg ${configured ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                  title={configured ? undefined : t('No está configurado en este entorno.')}
                >
                  <input
                    type="checkbox"
                    className="accent-brand-500"
                    checked={!!form.channels[channel]}
                    disabled={!configured}
                    onChange={(e) => update({ channels: { ...form.channels, [channel]: e.target.checked } })}
                  />
                  {channel === 'email' ? t('Correo') : t('WhatsApp')}
                </label>
              );
            })}
          </div>
          {savedNumberMissing && !form.channels.whatsapp && channelConfigured(delivery, 'whatsapp') && (
            <p className="text-xs text-status-warning">
              {t('El número de WhatsApp configurado no está disponible. Para enviar por WhatsApp, tildalo y elegí otro número.')}
            </p>
          )}
        </fieldset>

        {form.channels.whatsapp &&
          (selectableNumbers.length === 1 && form.whatsappIntegrationId === selectableNumbers[0].id ? (
            <div className="space-y-2">
              <p className="text-xs text-fg-muted">
                {t('WhatsApp sale desde {{numero}}.', {
                  numero: [selectableNumbers[0].label, selectableNumbers[0].phone].filter(Boolean).join(' · '),
                })}
              </p>
              <SelectedNumberWarnings option={selectableNumbers[0]} />
            </div>
          ) : (
            <WhatsappNumberSelect
              integrations={setup.integrations}
              loading={setup.integrationsLoading}
              error={setup.integrationsError}
              onRetry={setup.reloadIntegrations}
              value={form.whatsappIntegrationId}
              savedId={savedWhatsappId}
              onChange={(id) => update({ whatsappIntegrationId: id })}
            />
          ))}

        {templateDefaults && selectedChannels(form.channels).length > 0 && (
          <div className="space-y-2">
            <ReminderTemplateFields
              channels={selectedChannels(form.channels)}
              value={form.templateIds}
              onChange={(channel, templateId) => update({ templateIds: { ...form.templateIds, [channel]: templateId } })}
              templates={templates}
              defaults={templateDefaults}
              invalidChannels={invalidTemplates}
              disabled={submitting}
            />
            <p className="m-0 text-xs text-fg-muted">
              {t('Se usa la versión de hoy de cada plantilla: si después la editás o la archivás, este envío no cambia. Las notas y los montos se toman al momento de enviar.')}
            </p>
          </div>
        )}

        <p className="text-xs text-fg-muted">
          {t('Se envía aunque el comercio tenga pausados los automáticos. Si ya recibió un recordatorio por ese canal en las 24 h previas, se omite.')}
        </p>

        {alreadyScheduled && (
          <div
            className="flex items-start gap-1.5 rounded-md bg-status-warning-soft px-3 py-2 text-sm text-status-warning"
            role="alert"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div className="space-y-1">
              <p className="m-0">
                {alreadyScheduled.length > 0
                  ? t('No se programó nada: esas notas ya tienen un recordatorio pendiente por el mismo canal, programado para el {{fecha}}. Si querés cambiar la fecha u hora, cancelá ese programado en Programados y volvé a programar.', {
                      fecha: alreadyScheduled.join(', '),
                    })
                  : t('No se programó nada: esas notas ya tienen un recordatorio pendiente por el mismo canal en otro programado. Si querés cambiar la fecha u hora, cancelá ese programado en Programados y volvé a programar.')}
              </p>
              <Link to={SCHEDULES_PATH} className="inline-block text-brand-700 underline underline-offset-2">
                {t('Ir a Programados')}
              </Link>
            </div>
          </div>
        )}

        {error && (
          <p className="rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
    // Sin plantillas en billing no hay vista previa: queda solo el formulario. Con más notas
    // que el tope billing rechazaría el pedido: se avisa en el formulario.
    body = templateDefaults && !tooMany ? (
      <div className="grid gap-5 md:grid-cols-2">
        {fields}
        <ReminderSendPreview
          preview={preview}
          companyPicker={{ value: previewCompanyId, onChange: setPreviewCompanyId }}
        />
      </div>
    ) : (
      fields
    );
  }

  return (
    <Modal
      open={open}
      onClose={() => !submitting && onClose()}
      size="lg"
      title={t('Programar recordatorio')}
      description={t('{{n}} notas seleccionadas', { n: notes?.length ?? 0 })}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('Cancelar')}
          </Button>
          <Button
            onClick={handleSubmit}
            loading={submitting}
            disabled={blocked}
            iconLeft={<CalendarClock className="h-4 w-4" />}
          >
            {t('Programar')}
          </Button>
        </>
      }
    >
      {body}
    </Modal>
  );
}
